import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Link as MUILink,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import { API, Auth } from "aws-amplify";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  }).format(date);
};

const Dashboard = () => {
  const [overlay, setOverlay] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsCount, setProductsCount] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [sellerID, setSellerID] = useState("");
  const productPerPage = 20;

  useEffect(() => {
    const initialize = async () => {
      setOverlay(true);
      try {
        const user = await Auth.currentAuthenticatedUser();
        const userSub = user?.attributes?.sub;
        if (!userSub) return;
        setSellerID(userSub);
        await fetchAllData(userSub);
      } catch (error) {
        console.error("初期化エラー:", error);
        alert("初期化に失敗しました。");
      } finally {
        setOverlay(false);
      }
    };
    initialize();
  }, [page]);

  const fetchAllData = async (targetSellerID) => {
    try {
      // 商品一覧を取得
      const prodRes = await API.graphql({
        query: /* GraphQL */ `
          query ListProducts($limit: Int) {
            listProducts(limit: $limit) {
              items {
                id
                title
                price
                updatedAt
                sellerID
              }
            }
          }
        `,
        variables: { limit: 1000 },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const allProducts = prodRes.data.listProducts.items || [];
      const filtered = allProducts.filter((p) => p.sellerID === targetSellerID);
      setProductsCount(filtered.length);

      const startIndex = (page - 1) * productPerPage;
      const endIndex = page * productPerPage;
      setProducts(filtered.slice(startIndex, endIndex));

      // 顧客一覧（全件）を取得
      const allCustomers = [];
      let nextToken = null;
      do {
        const custRes = await API.graphql({
          query: /* GraphQL */ `
            query ListCustomers($limit: Int, $nextToken: String) {
              listCustomers(limit: $limit, nextToken: $nextToken) {
                items {
                  id
                  productID
                }
                nextToken
              }
            }
          `,
          variables: { limit: 1000, nextToken },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });

        const data = custRes.data.listCustomers;
        allCustomers.push(...(data.items || []));
        nextToken = data.nextToken;
      } while (nextToken);

      setCustomers(allCustomers);
    } catch (err) {
      console.error("データ取得エラー:", err);
    }
  };

  const getSalesData = (productID, price) => {
    const filtered = customers.filter((c) => c.productID === productID);
    return {
      salesCount: filtered.length,
      totalRevenue: filtered.length * price,
    };
  };

  const handleBack = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNext = () => {
    if (page * productPerPage < productsCount) setPage(page + 1);
  };

  return (
    <Box mx={6}>
      {overlay ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-start" my={3}>
            <Button
              variant="contained"
              color="primary"
              component={MUILink}
              href="https://dashboard.stripe.com/balance/overview"
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripeで残高を確認する
            </Button>
          </Box>

          <Typography variant="h6" gutterBottom>
            商品一覧（{productsCount} 件）
          </Typography>

          <TableContainer component={Paper} style={{ marginTop: "32px" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>商品名</TableCell>
                  <TableCell>価格 (円)</TableCell>
                  <TableCell>販売数</TableCell>
                  <TableCell>売上 (円)</TableCell>
                  <TableCell>更新日時</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => {
                  const { salesCount, totalRevenue } = getSalesData(product.id, product.price);
                  return (
                    <TableRow key={product.id}>
                      <TableCell>{product.title}</TableCell>
                      <TableCell>{product.price}</TableCell>
                      <TableCell>{salesCount}</TableCell>
                      <TableCell>{totalRevenue}</TableCell>
                      <TableCell>{formatDate(product.updatedAt)}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={8}>
                          <Button
                            variant="contained"
                            color="primary"
                            component={Link}
                            to={`/products/${product.id}`}
                          >
                            詳細を見る
                          </Button>
                          <Button
                            variant="contained"
                            color="secondary"
                            component={Link}
                            to={`/products/edit/${product.id}`}
                          >
                            商品編集
                          </Button>
                          <Button
                            variant="contained"
                            color="default"
                            component={Link}
                            to={`/dashboard/${product.id}`}
                          >
                            顧客データを見る
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {products.length > 0 && (
            <Box display="flex" justifyContent="center" my={4} gap={2}>
              <Button variant="contained" color="primary" disabled={page <= 1} onClick={handleBack}>
                前へ
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={page * productPerPage >= productsCount}
                onClick={handleNext}
              >
                次へ
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard;
